import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, getFirestore, onSnapshot } from "firebase/firestore";
import { useEventId } from "../EventContext";
import { toMillis } from "../utils/firestoreTime";
import { displayElapsed, durationToMs, msToDuration } from "../utils/finishTime";
import { AQUAFITX_BG, AQUAFITX_FONT, AQUAFITX_GOLD, useAquaFitXFont } from "../utils/aquafitxTheme";

/**
 * Live leaderboard for AquaFitX Race — a pool event timed by hand to the
 * hundredth, shown on the venue screens.
 *
 * What the generic venue board could not do for this event:
 *   - Individual age categories are ranked men and women separately, so the
 *     board shows both podiums side by side instead of one mixed list.
 *   - Races run category after category (individual, then doubles, then relay).
 *     The board follows the category of the most recently recorded time, so
 *     nobody has to switch it by hand. Tapping a category pins it; FOLLOW LIVE resumes.
 *     `?cat=<id>` pins a screen to one category for good.
 *   - Doubles and relays are team entries: the members are shown, not just a BIB.
 *   - Places are decided in hundredths, so the time and the gap to the leader
 *     are shown to the hundredth.
 *
 * `?demo=1` fills the board with made-up times, in the browser only — nothing
 * is written — so the screens can be checked before the first race.
 */

const GOLD = AQUAFITX_GOLD;
const OUT_STATUSES = new Set(["DNF", "DNS", "DSQ"]);
/** How long a newly posted time stays highlighted. */
const FRESH_MS = 30 * 1000;
/**
 * One type size for every category. Scaling the type to the field size made
 * every category look different, so instead a long field pages: at this size
 * 11 rows fit a column, and 30-39 men (19) shows two pages in turn.
 */
const FONT_U = 2.9;
const ROW_U = 5;
const PAGE_SIZE = 11;
const PAGE_MS = 8000;

/** Elapsed ms including any penalty, or null when the entry has no time. */
const totalMsOf = (result) => {
  const ms = durationToMs(String(result.recordedDuration || result.finishDuration || ""));
  if (!Number.isFinite(ms)) return null;
  return ms + (Number(result.penaltySeconds) || 0) * 1000;
};

/** Rank a list of results: fastest first, equal times share a place. */
const rankRows = (rows) => {
  const timed = rows
    .map((row) => ({ ...row, _ms: totalMsOf(row) }))
    .filter((row) => row._ms !== null && !OUT_STATUSES.has(String(row.status || "").toUpperCase()))
    .sort((a, b) => a._ms - b._ms);
  let place = 0;
  return timed.map((row, index) => {
    if (index === 0 || row._ms !== timed[index - 1]._ms) place = index + 1;
    return { ...row, _place: place, _gapMs: row._ms - timed[0]._ms };
  });
};

/** Gap to the leader, in the board's hh:mm:ss: +00:01:42. */
const gapText = (ms) => (ms ? `+${displayElapsed(msToDuration(ms))}` : "");


// Column widths shared by the rows and their header line, in board units.
const COL_W = { place: FONT_U * 2.1, bib: FONT_U * 2.4, gap: FONT_U * 3, time: FONT_U * 5.2 };

const Row = ({ row, fresh, onSelect, compact = false }) => {
  const fontVh = FONT_U;
  // Five full relay names do not fit one line at the board's type size, so a
  // relay row takes two lines rather than shrinking or cutting names off.
  const wraps = String(row.name || "").split(" / ").length > 2;
  const podium = row._place <= 3;
  return (
    <div
      // Clicking an athlete opens their category's tab (and leaves Follow
      // live), so a time seen in passing can be read in its own table.
      onClick={onSelect ? () => onSelect(row.categoryId) : undefined}
      className={`flex items-center ${compact ? "gap-[calc(var(--u)*0.8)] px-[calc(var(--u)*1)]" : "gap-[calc(var(--u)*1.2)] px-[calc(var(--u)*1.6)]"} rounded-[calc(var(--u)*0.8)] transition-colors duration-700 ${onSelect ? "cursor-pointer" : ""}`}
      style={{
        ...(wraps || compact
          ? { minHeight: `calc(var(--u) * ${ROW_U})`, paddingTop: "calc(var(--u) * 0.8)", paddingBottom: "calc(var(--u) * 0.8)" }
          : { height: `calc(var(--u) * ${ROW_U})` }),
        flexShrink: 0,
        background: fresh
          ? "rgba(246,184,0,0.28)"
          : podium
            ? "rgba(246,184,0,0.10)"
            : "rgba(4,16,58,0.55)",
        boxShadow: fresh ? `0 0 0 calc(var(--u) * 0.25) ${GOLD}` : "none",
      }}
    >
      <span
        className="shrink-0 text-center font-black italic tabular-nums"
        style={{
          // Two digits at most: on a phone the TV width left a wide empty gutter.
          width: `calc(${compact ? 3.6 : COL_W.place} * var(--u))`,
          fontSize: `calc(${fontVh * 1.15} * var(--u))`,
          color: podium ? GOLD : "rgba(255,255,255,0.75)",
        }}
      >
        {row._place}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block font-bold uppercase leading-[1.1] ${wraps ? "line-clamp-2" : "truncate"}`}
          style={{ fontSize: `calc(${fontVh} * var(--u))` }}
        >
          {row.name}
        </span>
        {/* Phone: BIB and gap under the name, as the Indonesia Ultra board
            does, so the name keeps the width. */}
        {compact && (
          <span className="block tabular-nums text-white/55 font-semibold leading-[1.3]" style={{ fontSize: `calc(${fontVh * 0.62} * var(--u))` }}>
            BIB {row.bib}{row._gapMs > 0 ? ` · ${gapText(row._gapMs)}` : ""}
          </span>
        )}
      </span>
      {!compact && (
        <span
          className="shrink-0 text-center tabular-nums text-white/55 font-semibold"
          style={{ width: `calc(${COL_W.bib} * var(--u))`, fontSize: `calc(${fontVh * 0.7} * var(--u))` }}
        >
          {row.bib}
        </span>
      )}
      {/* Gap to the leader in its own column, on the time's line: stacked
          under the time it collided with the next row in a long field. On a
          phone the column is too costly — names were cut — so it rides under
          the name (see above). */}
      {!compact && (
        <span
          className="shrink-0 text-right tabular-nums text-white/55 font-semibold"
          style={{ width: `calc(${COL_W.gap} * var(--u))`, fontSize: `calc(${fontVh * 0.6} * var(--u))` }}
        >
          {gapText(row._gapMs)}
        </span>
      )}
      <span className="shrink-0 text-right" style={{ width: `calc(${COL_W.time} * var(--u))` }}>
        <span
          className="block font-black italic tabular-nums leading-[1]"
          style={{ fontSize: `calc(${fontVh * 1.1} * var(--u))`, color: podium ? GOLD : "#fff" }}
        >
          {displayElapsed(msToDuration(row._ms))}
        </span>
      </span>
    </div>
  );
};

const Column = ({ title, rows, entries, now, onSelect, compact = false }) => {
  // On a phone the page scrolls, so every row is listed and nothing pages.
  const pages = compact ? 1 : Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // A time that just came in is shown wherever it landed; otherwise the pages
  // take turns.
  const freshIndex = rows.findIndex((row) => now - (toMillis(row.updatedAt) || 0) < FRESH_MS);
  const page = freshIndex >= 0 ? Math.floor(freshIndex / PAGE_SIZE) : Math.floor(now / PAGE_MS) % pages;
  const visible = compact ? rows : rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  return (
    <section
      className={`${compact ? "" : "min-h-0 flex-1"} flex flex-col rounded-[calc(var(--u)*1.4)] overflow-hidden border`}
      style={{ borderColor: "rgba(246,184,0,0.35)", background: "rgba(2,10,40,0.55)", backdropFilter: "blur(6px)" }}
    >
      {title && (
        // Column labels ride in this bar, over their columns, rather than on a
        // line of their own: spectators asked what the small numbers were, and
        // the board has no row to spare. Right padding = the rows' inset
        // (container 1u + row 1.6u) so each label sits over its column.
        <header
          className="shrink-0 flex items-baseline gap-[calc(var(--u)*1.2)] pl-[calc(var(--u)*2)] pr-[calc(var(--u)*2.6)] py-[calc(var(--u)*1.1)] whitespace-nowrap"
          style={{ background: "rgba(246,184,0,0.92)", color: "#041036" }}
        >
          <h3 className="shrink-0 font-black italic uppercase text-[calc(var(--u)*3)] leading-[1.1] pr-[0.2em]">{title}</h3>
          <span className="min-w-0 flex-1 truncate font-bold text-[calc(var(--u)*1.7)] tabular-nums">
            {pages > 1 && `PAGE ${page + 1}/${pages} · `}
            {rows.length} / {entries} FINISHED
          </span>
          {(compact ? [["TIME", COL_W.time, "text-right"]] : [["BIB", COL_W.bib, "text-center"], ["GAP", COL_W.gap, "text-right"], ["TIME", COL_W.time, "text-right"]]).map(([label, w, align]) => (
            <span
              key={label}
              className={`shrink-0 font-bold text-[calc(var(--u)*1.7)] ${align}`}
              style={{ width: `calc(${w} * var(--u))` }}
            >
              {label}
            </span>
          ))}
        </header>
      )}
      <div className={`${compact ? "" : "flex-1 min-h-0"} flex flex-col gap-[calc(var(--u)*0.6)] p-[calc(var(--u)*1)]`}>
        {visible.map((row) => (
          <Row
            key={row.bib}
            row={row}
            fresh={now - (toMillis(row.updatedAt) || 0) < FRESH_MS}
            onSelect={onSelect}
            compact={compact}
          />
        ))}
        {!rows.length && (
          <div className={`flex-1 flex items-center justify-center text-white/50 font-bold italic uppercase text-[calc(var(--u)*3)] ${compact ? "py-[calc(var(--u)*4)]" : ""}`}>
            Waiting for the first time
          </div>
        )}
      </div>
    </section>
  );
};

export const AquaFitXBoard = () => {
  useAquaFitXFont();
  const { realEventId: eventId } = useEventId();
  const [searchParams] = useSearchParams();
  const lockedCategory = searchParams.get("cat");
  const demo = searchParams.get("demo") === "1";

  const [event, setEvent] = useState(null);
  const [participantList, setParticipantList] = useState([]);
  const [results, setResults] = useState([]);
  const [pinned, setPinned] = useState(lockedCategory);
  // Phone layout: below 768px the TV board (sized to a 16:9 screen) became
  // unreadably small, so it switches to a fixed unit, stacked columns and a
  // page that scrolls.
  const [compact, setCompact] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setCompact(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Open a category's tab, as its pill in the footer does. A screen locked
  // with ?cat= stays where it was put.
  const selectCategory = (categoryId) => {
    if (!lockedCategory && categoryId) setPinned(categoryId);
  };
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!eventId) return;
    // The event document straight from Firestore: the API may be asleep.
    getDoc(doc(getFirestore(), "event_timing", eventId))
      .then((snapshot) => setEvent(snapshot.exists() ? snapshot.data() : null))
      .catch(() => {});
    // Straight from Firestore: the participants API leaves out the team
    // members, which is what this board shows for doubles and relays.
    getDocs(collection(getFirestore(), "event_timing", eventId, "participants"))
      .then((snapshot) => setParticipantList(snapshot.docs.map((doc) => doc.data())))
      .catch(() => {});
  }, [eventId]);

  useEffect(() => {
    if (!eventId || !demo || !participantList.length) return;
    // Deterministic per BIB so the demo does not reshuffle on every render.
    const seed = (bib) => ((Number(bib) * 9301 + 49297) % 233280) / 233280;
    const start = Date.now();
    setResults(
      participantList
        .filter((p) => seed(p.bib) < 0.8)
        .map((p, i) => ({
          bib: String(p.bib),
          name: p.name,
          gender: p.gender,
          categoryId: p.categoryId,
          recordedDuration: msToDuration(Math.round((70 + seed(p.bib * 7) * 50) * 100) * 10),
          updatedAt: new Date(start - (participantList.length - i) * 4000).toISOString(),
        })),
    );
  }, [eventId, demo, participantList]);

  useEffect(() => {
    if (!eventId || demo) return;
    return onSnapshot(collection(getFirestore(), "event_timing", eventId, "results"), (snapshot) =>
      setResults(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
    );
  }, [eventId, demo]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const categories = event?.categories || [];
  const participants = useMemo(
    () => new Map(participantList.map((p) => [String(p.bib), p])),
    [participantList],
  );

  // The most recent time posted anywhere: what FOLLOW LIVE follows, and the ticker.
  const latest = useMemo(() => {
    let best = null;
    for (const result of results) {
      if (totalMsOf(result) === null) continue;
      const at = toMillis(result.updatedAt) || 0;
      if (!best || at > best.at) best = { result, at };
    }
    return best;
  }, [results]);

  const categoryId = pinned || latest?.result.categoryId || categories[0]?.id;
  const category = categories.find((c) => c.id === categoryId) || categories[0] || null;

  const categoryResults = results.filter((r) => r.categoryId === category?.id);
  const entriesOf = (gender) =>
    participantList.filter((p) => p.categoryId === category?.id && (!gender || p.gender === gender)).length;
  const genderOf = (r) => participants.get(String(r.bib))?.gender || r.gender;
  // Individual categories are ranked men and women separately; teams (one
  // gender per category) are a single list.
  const isIndividualCat = (id) =>
    !participantList.some((p) => p.categoryId === id && p.members?.length);
  const isIndividual = isIndividualCat(category?.id);
  const menRows = rankRows(categoryResults.filter((r) => genderOf(r) === "male"));
  const womenRows = rankRows(categoryResults.filter((r) => genderOf(r) === "female"));


  return (
    <main
      className={`${compact ? "min-h-screen w-full pb-[calc(var(--u)*9)]" : "h-screen w-screen overflow-hidden"} text-white flex flex-col`}
      style={{
        // One unit for every size on the board: 1% of the height of the
        // largest 16:9 box that fits the screen. Sized on height alone, a
        // narrower or portrait screen overflowed sideways and names, times and
        // the title were cut off.
        "--u": compact ? "5.5px" : "min(1vh, 0.5625vw)",
        fontFamily: AQUAFITX_FONT,
        backgroundColor: "#041036",
        backgroundImage: AQUAFITX_BG,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: compact ? "fixed" : undefined,
      }}
    >
      <header className={`shrink-0 flex items-center justify-between px-[calc(var(--u)*3)] pt-[calc(var(--u)*2)] pb-[calc(var(--u)*1.2)] ${compact ? "flex-wrap" : ""}`}>
        <div className="leading-none">
          <div className="font-black italic uppercase text-[calc(var(--u)*4.4)] tracking-tight">
            AquaFit<span style={{ color: GOLD }}>X</span> Race
          </div>
          <div className="text-[calc(var(--u)*1.7)] font-semibold text-white/70 mt-[calc(var(--u)*0.4)]">
            LIVE LEADERBOARD
          </div>
        </div>
        <div className={compact ? "order-last w-full min-w-0 mt-[calc(var(--u)*1.5)]" : "text-center min-w-0 px-[calc(var(--u)*2)]"}>
          <div className="text-[calc(var(--u)*1.8)] font-bold" style={{ color: GOLD }}>
            {pinned ? "CATEGORY" : "NOW RACING"}
          </div>
          {/* Padding inside the clipping box: a heavy italic leans past its
              own advance width, and truncate's overflow:hidden cut the last
              letter and the tops off. */}
          <h1 className={`font-black italic uppercase text-[calc(var(--u)*6)] leading-[1.1] truncate pt-[0.08em] ${compact ? "pr-[0.25em]" : "px-[0.25em]"}`}>
            {category?.name || "—"}
          </h1>
        </div>
        <div className="text-right leading-none">
          <div className="font-black italic tabular-nums text-[calc(var(--u)*5)]">
            {new Date(now).toLocaleTimeString("en-GB", { hour12: false })}
          </div>
          <div className="text-[calc(var(--u)*1.7)] font-semibold text-white/70 mt-[calc(var(--u)*0.4)]">
            {categoryResults.filter((r) => totalMsOf(r) !== null).length} / {entriesOf()} FINISHED
          </div>
        </div>
      </header>

      <div className={`${compact ? "flex-col" : "flex-1 min-h-0"} flex gap-[calc(var(--u)*1.6)] px-[calc(var(--u)*3)] pb-[calc(var(--u)*1.4)]`}>
        {isIndividual ? (
          <>
            <Column
              title="Men"
              rows={menRows}
              entries={entriesOf("male")}
              now={now}
              onSelect={selectCategory}
              compact={compact}
            />
            <Column
              title="Women"
              rows={womenRows}
              entries={entriesOf("female")}
              now={now}
              onSelect={selectCategory}
              compact={compact}
            />
          </>
        ) : (
          <Column
            title="Teams"
            rows={rankRows(categoryResults)}
            entries={entriesOf()}
            now={now}
            onSelect={selectCategory}
            compact={compact}
          />
        )}
      </div>

      <footer
        // Phone: pinned to the bottom of the screen. Sticky, it sat after the
        // content on a short category and at the screen edge on a long one, so
        // the tabs jumped up and down as categories changed.
        className={`shrink-0 flex items-center gap-[calc(var(--u)*0.8)] px-[calc(var(--u)*3)] pb-[calc(var(--u)*1.6)] overflow-x-auto ${compact ? "fixed bottom-0 left-0 right-0 z-10 pt-[calc(var(--u)*1.2)]" : ""}`}
        style={compact ? { background: "rgba(3,10,40,0.92)" } : undefined}
      >
        {!lockedCategory && (
          <button
            onClick={() => setPinned(null)}
            className="shrink-0 rounded-full px-[calc(var(--u)*1.6)] py-[calc(var(--u)*0.5)] text-[calc(var(--u)*1.7)] font-bold uppercase"
            style={!pinned ? { background: GOLD, color: "#041036" } : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }}
          >
            Follow live
          </button>
        )}
        {categories.map((item) => (
          <button
            key={item.id}
            onClick={() => !lockedCategory && setPinned(item.id)}
            className="shrink-0 rounded-full px-[calc(var(--u)*1.6)] py-[calc(var(--u)*0.5)] text-[calc(var(--u)*1.7)] font-bold uppercase whitespace-nowrap"
            style={
              item.id === category?.id
                ? { background: pinned ? GOLD : "rgba(246,184,0,0.25)", color: pinned ? "#041036" : GOLD }
                : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }
            }
          >
            {item.name.replace(/^INDIVIDUAL |^TEAM /, "")}
          </button>
        ))}
      </footer>
    </main>
  );
};

export default AquaFitXBoard;
